<!--
  MARA tab, ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspaceChat.vue: top bar (Chat History
  popover, Files, New Chat), the message list or the "Start a new chat" empty state, and ChatInput at the bottom.
  The chat is local (src/mara/chat.ts): history holds this session's chats, and Files shows the shared nudge
  because chat files are stored in a Nanome workspace.
-->
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import Button from 'primevue/button';
import Popover from 'primevue/popover';
import { useMaraChat, type ChatMessage } from '@/mara/chat';
import TeaserCta from '../TeaserCta.vue';
import ChatInput from './ChatInput.vue';
import MessageList from './MessageList.vue';

const chat = useMaraChat();
const chatsPopover = ref<InstanceType<typeof Popover>>();
const scroller = ref<HTMLElement | null>(null);
const showFiles = ref(false);

const messages = computed<ChatMessage[]>(() => {
  const all = chat.messages.slice();
  if (chat.status !== null) all.push({ id: 'status', type: 'tool', role: 'agent', content: chat.status, created_at: new Date() });
  return all;
});

// keep the newest message in view while the exchange plays (useScrollPin in prod)
// (busy too: the example buttons come back when a reply ends, which shortens the list)
watch(() => [messages.value.length, messages.value[messages.value.length - 1]?.content, chat.busy, chat.selectedId], async () => {
  await nextTick();
  const el = scroller.value;
  if (el) el.scrollTop = el.scrollHeight;
});

function selectChat(id: string) {
  chatsPopover.value?.hide();
  showFiles.value = false;
  chat.setSelected(id);
}

function newChat() {
  showFiles.value = false;
  chat.newChat();
}

function fromNow(date: Date) {
  const s = Math.round((Date.now() - date.getTime()) / 1000);
  if (s < 45) return 'a few seconds ago';
  const m = Math.round(s / 60);
  if (m < 45) return m <= 1 ? 'a minute ago' : `${m} minutes ago`;
  const h = Math.round(m / 60);
  return h <= 1 ? 'an hour ago' : `${h} hours ago`;
}
</script>

<template>
  <div class="workspace-chat flex-1 flex flex-col min-h-0" data-test="mara-tab">
    <!-- Top bar -->
    <div v-if="!showFiles" class="p-4 flex items-center gap-2 shrink-0">
      <Button class="mr-auto" severity="secondary" size="small" rounded data-test="chat-history" @click="chatsPopover?.toggle($event)">
        <i class="i-tabler-messages shrink-0" />
        <span class="truncate">
          {{ chat.selected?.title || 'Chat History' }}
        </span>
        <i class="i-tabler-chevron-down shrink-0 op-50" />
      </Button>
      <Button
        v-tooltip.bottom="'Files'"
        icon="i-tabler-files"
        severity="secondary"
        size="small"
        rounded
        :disabled="!chat.selected"
        @click="showFiles = !showFiles"
      />
      <Button
        v-tooltip.bottom="'New Chat'"
        icon="i-tabler-plus"
        severity="secondary"
        size="small"
        rounded
        :disabled="chat.busy"
        data-test="chat-new"
        @click="newChat"
      />
    </div>

    <!-- Workspace chats popover -->
    <Popover ref="chatsPopover" class="w-20rem rd-4">
      <div class="max-h-60 of-y-auto flex flex-col gap-1">
        <div v-if="!chat.history.length" class="px-2 py-4 text-center op-50 text-sm">
          No chats yet
        </div>
        <Button
          v-for="c of chat.history"
          :key="c.id"
          :class="{ 'bg-section': c.id === chat.selectedId }"
          class="group flex items-center gap-2 p-2 rd-2!"
          severity="secondary"
          text
          @click="selectChat(c.id)"
        >
          <i class="i-tabler-messages shrink-0 op-50" />
          <div class="flex-1 min-w-0 flex flex-col text-left">
            <div class="w-full truncate text-sm">
              {{ c.title || 'Untitled' }}
            </div>
            <div class="op-50 text-xs">
              {{ fromNow(c.last_message_at) }}
            </div>
          </div>
        </Button>
      </div>
    </Popover>

    <!-- Files view -->
    <template v-if="showFiles">
      <div class="p-4 shrink-0">
        <Button icon="i-tabler-arrow-left" label="Back" severity="secondary" size="small" rounded @click="showFiles = false" />
      </div>
      <div class="flex-1 min-h-0 flex items-center justify-center p-4">
        <TeaserCta title="Chat files live in your Nanome workspace" text="Structures, tables and images MARA makes in a chat appear here, ready to load into the scene." />
      </div>
    </template>

    <!-- Chat messages -->
    <template v-else-if="chat.selected">
      <div ref="scroller" class="flex-1 min-h-0 px-4 pb-8 of-y-auto" data-test="chat-messages">
        <MessageList :messages="messages" />
      </div>
    </template>

    <!-- Empty state -->
    <template v-else>
      <div class="flex-1 min-h-0 flex items-center justify-center op-50" data-test="chat-empty">
        <div class="text-center">
          <i class="i-tabler-messages text-4xl mb-2" />
          <div class="text-sm">
            Start a new chat
          </div>
        </div>
      </div>
    </template>

    <div v-if="!showFiles" class="shrink-0">
      <ChatInput />
    </div>
  </div>
</template>
